import { useQuery } from '@tanstack/react-query'
import { Modal } from 'antd'
import { useRef } from 'react'
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
  useWatch,
} from 'react-hook-form'

import { segmentsQueries } from '@/api/segments/segmentsQueries'
import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import LabelHint from '@/components/Atoms/LabelHint/LabelHint'
import type { FormInputControlType, FormInputRegisterType } from '@/types'

import {
  getRrcChangeMessage,
  MARKUP_HINT,
  RRC_HINT,
  type SegmentFormType,
} from '../SegmentCreationModal.config'
import cn from '../SegmentCreationModal.module.scss'

interface SegmentPricingDefaultsProps {
  control: Control<SegmentFormType>
  register: UseFormRegister<SegmentFormType>
  errors: FieldErrors<SegmentFormType>
  editingSegmentId: string | null
  isRrcLocked: boolean
  isOrganisationSelected: boolean
}

function SegmentPricingDefaults({
  control,
  register,
  errors,
  editingSegmentId,
  isRrcLocked,
  isOrganisationSelected,
}: SegmentPricingDefaultsProps) {
  const [modal, contextHolder] = Modal.useModal()
  const isConfirmOpenRef = useRef(false)
  const segmentsQuery = useQuery(segmentsQueries.list('mine'))
  const description = useWatch({ control, name: 'description' })
  const currentRrcSegment = segmentsQuery.data?.find(
    (segment) => segment.isRrc && segment.id !== editingSegmentId
  )

  const handleRrcChange = (
    checked: boolean,
    onChange: (value: boolean) => void
  ) => {
    if (!checked || !currentRrcSegment) {
      onChange(checked)
      return
    }
    if (isConfirmOpenRef.current) return
    isConfirmOpenRef.current = true
    modal.confirm({
      title: 'Смена сегмента РРЦ',
      content: getRrcChangeMessage(description, currentRrcSegment.description),
      okText: 'Продолжить',
      cancelText: 'Отмена',
      centered: true,
      onOk: () => onChange(true),
      afterClose: () => {
        isConfirmOpenRef.current = false
      },
    })
  }

  return (
    <>
      {contextHolder}
      <AtomFormInput
        layout="stacked"
        idPrefix="segment-"
        isOrganisationSelected={isOrganisationSelected}
        location="segmentCreation"
        name="markupPercentage"
        title={
          <>
            Наценка по умолчанию для загрузки цен, %
            <LabelHint text={MARKUP_HINT} />
          </>
        }
        placeholder="0"
        inputMode="decimal"
        errors={errors}
        register={register as FormInputRegisterType}
        control={control as FormInputControlType}
        required
      />

      <div className={cn.checkboxWithHint}>
        <Controller
          control={control}
          name="isRrc"
          render={({ field }) => (
            <AtomCheckbox
              checked={field.value}
              onChange={(checked) => handleRrcChange(checked, field.onChange)}
              disabled={isRrcLocked}
              isOrganisationSelected={isOrganisationSelected}
            >
              Из этого сегмента берётся РРЦ
            </AtomCheckbox>
          )}
        />
        <LabelHint text={RRC_HINT} />
      </div>
    </>
  )
}

export default SegmentPricingDefaults
